import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signIn, signOut } from '../util/amplifyAuth';
import { manageActivation } from "../components/API";
import { completeSignInAndRedirect } from "../util/authNav";
import { getClinicTheme } from "../config/clinic-themes";
import Spinner from "../components/Spinner/Spinner";
import PasswordRequirements, { usePasswordValidation } from "../components/PasswordRequirements";
import "../styles/ActivateAccount.css";

function ActivateAccount() {
    const [step, setStep] = useState('loading'); // loading | form | success | error | locked
    const [token, setToken] = useState(null);
    const [clinicInfo, setClinicInfo] = useState({ institution: '', maskedEmail: '' });
    const [theme, setTheme] = useState(getClinicTheme(null));
    const [errorMessage, setErrorMessage] = useState('');

    // Form fields
    const [dob, setDob] = useState('');
    const [last4phone, setLast4phone] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [attemptsRemaining, setAttemptsRemaining] = useState(null);

    // Resend link
    const [resendEmail, setResendEmail] = useState('');
    const [resendSent, setResendSent] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Edge case #2: Inject no-referrer meta tag to prevent token leaking via Referer header
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'referrer';
        meta.content = 'no-referrer';
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    // Extract token from URL on mount, then clear it from the address bar
    useEffect(() => {
        const tokenParam = searchParams.get('token');
        if (!tokenParam) {
            setErrorMessage('No activation token provided. Please use the link from your email.');
            setStep('error');
            return;
        }

        setToken(tokenParam);

        // Edge case #2: Clear token from URL immediately
        window.history.replaceState({}, '', '/activate');

        validateToken(tokenParam);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const validateToken = async (tokenValue) => {
        try {
            const result = await manageActivation('validate-token', { token: tokenValue });

            if (result.valid && result.type !== 'password-reset') {
                const clinicTheme = getClinicTheme(result.clinicInstitution);
                setTheme(clinicTheme);
                setClinicInfo({
                    institution: result.clinicInstitution || '',
                    maskedEmail: result.maskedEmail || ''
                });
                setStep('form');
            } else {
                switch (result.reason) {
                    case 'not_found':
                        setErrorMessage('This activation link is invalid. Please check your email for the correct link.');
                        break;
                    case 'expired':
                        setErrorMessage('This activation link has expired. Please request a new one below.');
                        break;
                    case 'used':
                        setErrorMessage('This activation link has already been used. If you need to log in, go to the login page.');
                        break;
                    case 'locked':
                        setStep('locked');
                        return;
                    default:
                        setErrorMessage('This activation link is invalid.');
                }
                setStep('error');
            }
        } catch (err) {
            setErrorMessage('Unable to validate your activation link. Please try again later.');
            setStep('error');
        }
    };

    const { valid: passwordValid } = usePasswordValidation(password);

    const handleSubmit = async () => {
        setFormError('');

        // Client-side validation
        if (!dob) {
            setFormError('Please enter your date of birth.');
            return;
        }
        if (!/^\d{4}$/.test(last4phone)) {
            setFormError('Please enter the last 4 digits of your phone number.');
            return;
        }
        if (!passwordValid) {
            setFormError('Please meet all password requirements.');
            return;
        }
        if (password !== confirmPassword) {
            setFormError('Passwords do not match.');
            return;
        }

        setSubmitting(true);

        try {
            const result = await manageActivation('verify-identity', {
                token,
                dob,
                last4phone,
                newPassword: password
            });

            if (result.success) {
                // Auto-sign-in with the credentials just created
                try {
                    try { await signOut(); } catch {}
                    await signIn({ username: result.email, password });
                    await completeSignInAndRedirect(navigate);
                    return;
                } catch (signInErr) {
                    console.error('Auto-sign-in failed:', signInErr);
                    navigate('/login');
                    return;
                }
            } else if (result.error === 'verification_failed') {
                setAttemptsRemaining(result.attemptsRemaining);
                if (result.attemptsRemaining === 0) {
                    setStep('locked');
                } else {
                    setFormError(`Verification failed. ${result.attemptsRemaining} attempt${result.attemptsRemaining !== 1 ? 's' : ''} remaining.`);
                }
            } else if (result.error === 'password_policy') {
                setFormError(result.message || 'Password does not meet requirements.');
            } else if (result.error === 'invalid_token') {
                switch (result.reason) {
                    case 'expired':
                        setErrorMessage('This activation link has expired. Please request a new one below.');
                        setStep('error');
                        break;
                    case 'used':
                        setErrorMessage('This activation link has already been used.');
                        setStep('error');
                        break;
                    case 'locked':
                        setStep('locked');
                        break;
                    default:
                        setErrorMessage('This activation link is no longer valid.');
                        setStep('error');
                }
            } else {
                setFormError('Something went wrong. Please try again.');
            }
        } catch (err) {
            setFormError('Unable to complete activation. Please try again later.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResend = async () => {
        if (!resendEmail) return;

        setResendLoading(true);
        try {
            await manageActivation('resend-link', { email: resendEmail });
        } catch (err) {
            // Always show success to prevent email enumeration
        }
        setResendSent(true);
        setResendLoading(false);
    };

    // Apply clinic theme as CSS custom properties
    const themeStyle = {
        '--clinic-primary': theme.primary,
        '--clinic-accent': theme.accent,
        '--clinic-bg': theme.background,
    };

    // Loading state
    if (step === 'loading') {
        return (
            <div className="activate-page" style={themeStyle}>
                <div className="activate-container">
                    <div className="activate-loading">
                        <Spinner size={40} color={theme.primary} />
                        <p>Validating your activation link...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (step === 'success') {
        return (
            <div className="activate-page" style={themeStyle}>
                <div className="activate-container">
                    <div className="activate-success">
                        <div className="success-icon">&#10003;</div>
                        <h2>Account Activated</h2>
                        <p>
                            Your account is ready. You can now log in with your email and the password you just created.
                        </p>
                        <button className="btn-login" onClick={() => navigate('/login')}>
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Locked state
    if (step === 'locked') {
        return (
            <div className="activate-page" style={themeStyle}>
                <div className="activate-container">
                    <div className="activate-locked">
                        <div className="locked-icon">&#128274;</div>
                        <h2>Account Locked</h2>
                        <p>
                            Too many failed verification attempts. Please contact your clinic to request a new activation link.
                        </p>
                    </div>
                    <div className="resend-section">
                        <p>Or request a new activation link:</p>
                        <div className="resend-form">
                            <input
                                type="email"
                                placeholder="Your email address"
                                value={resendEmail}
                                onChange={(e) => { setResendEmail(e.target.value); setResendSent(false); }}
                            />
                            <button onClick={handleResend} disabled={resendLoading || !resendEmail}>
                                {resendLoading ? 'Sending...' : 'Resend'}
                            </button>
                        </div>
                        {resendSent && (
                            <p className="resend-confirmation">
                                If an account exists, a new activation link has been sent.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (step === 'error') {
        return (
            <div className="activate-page" style={themeStyle}>
                <div className="activate-container">
                    <h1>Activation</h1>
                    <div className="activate-error">{errorMessage}</div>

                    <div className="resend-section">
                        <p>Need a new activation link?</p>
                        <div className="resend-form">
                            <input
                                type="email"
                                placeholder="Your email address"
                                value={resendEmail}
                                onChange={(e) => { setResendEmail(e.target.value); setResendSent(false); }}
                            />
                            <button onClick={handleResend} disabled={resendLoading || !resendEmail}>
                                {resendLoading ? 'Sending...' : 'Resend'}
                            </button>
                        </div>
                        {resendSent && (
                            <p className="resend-confirmation">
                                If an account exists, a new activation link has been sent.
                            </p>
                        )}
                    </div>

                    <button
                        onClick={() => navigate('/login')}
                        style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'center',
                            padding: '12px',
                            marginTop: '16px',
                            color: '#6b7280',
                            fontSize: '14px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    // Form state
    return (
        <div className="activate-page" style={themeStyle}>
            <div className="activate-container">
                {/* Clinic Branding */}
                {(theme.logoUrl || theme.displayName) && (
                    <div className="clinic-branding">
                        {theme.logoUrl && (
                            <img src={theme.logoUrl} alt="" className="clinic-logo" />
                        )}
                        {theme.displayName && (
                            <p className="clinic-name">{theme.displayName}</p>
                        )}
                    </div>
                )}

                <h1>Activate Your Account</h1>
                <p className="activate-subtitle">
                    Verify your identity to complete your account setup
                    {clinicInfo.maskedEmail && <> for <strong>{clinicInfo.maskedEmail}</strong></>}
                </p>

                {formError && <div className="activate-error">{formError}</div>}

                {attemptsRemaining !== null && attemptsRemaining <= 2 && attemptsRemaining > 0 && (
                    <div className="activate-warning">
                        {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining before your account is locked.
                    </div>
                )}

                <form className="activate-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                    <div className="inline-fields">
                        <div className="form-group">
                            <label htmlFor="activate-dob">Date of Birth</label>
                            <input
                                id="activate-dob"
                                type="date"
                                value={dob}
                                onChange={(e) => setDob(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="activate-phone">Last 4 of Phone</label>
                            <input
                                id="activate-phone"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength="4"
                                placeholder="1234"
                                value={last4phone}
                                onChange={(e) => setLast4phone(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="activate-password">Create Password</label>
                        <input
                            id="activate-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <PasswordRequirements password={password} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="activate-confirm">Confirm Password</label>
                        <input
                            id="activate-confirm"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    <button
                        className="activate-submit"
                        type="submit"
                        disabled={submitting}
                    >
                        {submitting ? <Spinner size={20} color="#fff" /> : 'Activate Account'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ActivateAccount;
